import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { mandatesAPI, signaturesAPI } from '../../lib/api';
import { formatCurrency, formatDate, formatDateTime, MANDATE_STATUS_LABELS, MANDATE_TYPE_LABELS, getStatusColor } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import usePersistedTab from '../../hooks/usePersistedTab';
import { Progress } from '../../components/ui/progress';
import { Skeleton } from '../../components/ui/skeleton';
import { Separator } from '../../components/ui/separator';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import DVFAnalyzer from '../../components/dvf/DVFAnalyzer';
import {
    ArrowLeft,
    FileText,
    Home,
    User,
    Calendar,
    Euro,
    PenTool,
    Clock,
    CheckCircle,
    XCircle,
    Send,
    Download,
    Edit,
    TrendingDown,
    MessageSquare,
    HandCoins,
} from 'lucide-react';
import MakeOfferDialog from '../../components/offers/MakeOfferDialog';

function MandateDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isAdmin, isAgent, isSeller } = useAuth();
    const [mandate, setMandate] = useState(null);
    const [timeline, setTimeline] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sendingForSignature, setSendingForSignature] = useState(false);
    const [showAvenantDialog, setShowAvenantDialog] = useState(false);
    const [avenantData, setAvenantData] = useState({ newPrice: '', reason: '' });
    const [showMakeOfferDialog, setShowMakeOfferDialog] = useState(false);
    const [activeTab, setActiveTab] = usePersistedTab(
        'homly_mandate_detail_v1_tab',
        'details',
        ['details', 'sellers', 'dvf', 'timeline', 'documents'],
    );

    useEffect(() => {
        loadMandate();
    }, [id]);

    const loadMandate = async () => {
        try {
            const [mandateRes, timelineRes] = await Promise.all([
                mandatesAPI.getById(id),
                mandatesAPI.getTimeline(id)
            ]);
            setMandate(mandateRes.data);
            setTimeline(timelineRes.data.events || []);
        } catch (err) {
            toast.error('Erreur lors du chargement du mandat');
            navigate(-1);
        } finally {
            setLoading(false);
        }
    };

    const sendForSignature = async () => {
        setSendingForSignature(true);
        try {
            await mandatesAPI.sendForSignature(id);
            toast.success('Mandat envoyé pour signature !');
            loadMandate();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
        } finally {
            setSendingForSignature(false);
        }
    };

    const createAvenant = async () => {
        if (!avenantData.newPrice || !avenantData.reason) {
            toast.error('Veuillez remplir tous les champs');
            return;
        }
        try {
            await mandatesAPI.createAvenant(id, parseFloat(avenantData.newPrice), avenantData.reason);
            toast.success('Avenant créé !');
            setShowAvenantDialog(false);
            setAvenantData({ newPrice: '', reason: '' });
            loadMandate();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la création');
        }
    };

    const getProgressValue = (status) => {
        // Map exhaustif des statuts mandate_v2 (DB) ET v1 (legacy fr) pour ne rater aucun cas
        const progressMap = {
            // v2 (DB actuelle)
            draft: 10,
            sent: 25,
            acknowledged: 40,
            active: 50,
            offer: 65,
            offre: 65,
            compromise: 80,
            compromis: 80,
            sold: 100,
            closed: 100,
            terminated: 0,
            denounced: 0,
            expired: 0,
            // v1 (legacy fr)
            brouillon: 10,
            a_signer: 20,
            en_signature: 30,
            actif: 50,
            vendu: 100,
            denonce: 0,
            expire: 0,
        };
        return progressMap[status] || 0;
    };

    const basePath = isAdmin ? '/admin' : isAgent ? '/agent' : '/seller';

    if (loading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-12 w-64" />
                <Skeleton className="h-64 w-full" />
            </div>
        );
    }

    if (!mandate) {
        return (
            <div className="text-center py-12">
                <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h2 className="text-xl font-semibold mb-2">Mandat non trouvé</h2>
                <Button variant="outline" onClick={() => navigate(-1)}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Retour
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="mandate-detail-page">
            {/* Header */}
            <div className="flex flex-col gap-3">
                <div className="flex items-start gap-2">
                    <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="flex-shrink-0 -ml-2">
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h1 className="text-xl sm:text-2xl font-bold text-foreground truncate">
                                {mandate.mandate_number}
                            </h1>
                            <Badge className={getStatusColor(mandate.status)}>
                                {MANDATE_STATUS_LABELS[mandate.status] || mandate.status}
                            </Badge>
                        </div>
                        <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                            {MANDATE_TYPE_LABELS[mandate.mandate_type] || '—'} • Créé le {formatDate(mandate.created_at)}
                        </p>
                    </div>
                </div>
                
                {(isAdmin || isAgent) && (
                    <div className="flex gap-2 flex-wrap">
                        {/* CTA primaire : Faire une offre */}
                        {['active', 'actif', 'sent', 'acknowledged', 'offre', 'compromis', 'compromise'].includes(mandate.status) && (
                            <Button
                                onClick={() => setShowMakeOfferDialog(true)}
                                className="bg-gradient-to-r from-[#681793] to-[#FF914D] text-white shadow-lg shadow-[#FF914D]/30"
                                data-testid="make-offer-button"
                            >
                                <HandCoins className="w-4 h-4 mr-2" />
                                Faire une offre
                            </Button>
                        )}
                        {mandate.status === 'brouillon' && (
                            <Button 
                                onClick={sendForSignature}
                                disabled={sendingForSignature}
                                data-testid="send-for-signature-button"
                            >
                                <Send className="w-4 h-4 mr-2" />
                                {sendingForSignature ? 'Envoi...' : 'Envoyer pour signature'}
                            </Button>
                        )}
                        {['actif', 'offre', 'active', 'acknowledged', 'sent'].includes(mandate.status) && (
                            <Dialog open={showAvenantDialog} onOpenChange={setShowAvenantDialog}>
                                <DialogTrigger asChild>
                                    <Button variant="outline" data-testid="create-avenant-button">
                                        <TrendingDown className="w-4 h-4 mr-2" />
                                        Proposer une baisse
                                    </Button>
                                </DialogTrigger>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle>Créer un avenant</DialogTitle>
                                        <DialogDescription>
                                            Proposez une modification du prix. Un avenant sera généré pour signature.
                                        </DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-4">
                                        <div>
                                            <Label>Prix actuel</Label>
                                            <p className="text-lg font-semibold">{formatCurrency(mandate.price)}</p>
                                        </div>
                                        <div>
                                            <Label htmlFor="newPrice">Nouveau prix</Label>
                                            <Input
                                                id="newPrice"
                                                type="number"
                                                value={avenantData.newPrice}
                                                onChange={(e) => setAvenantData({ ...avenantData, newPrice: e.target.value })}
                                                placeholder="350000"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="reason">Raison</Label>
                                            <Textarea
                                                id="reason"
                                                value={avenantData.reason}
                                                onChange={(e) => setAvenantData({ ...avenantData, reason: e.target.value })}
                                                placeholder="Ajustement suite aux retours du marché..."
                                            />
                                        </div>
                                    </div>
                                    <DialogFooter>
                                        <Button variant="outline" onClick={() => setShowAvenantDialog(false)}>
                                            Annuler
                                        </Button>
                                        <Button onClick={createAvenant}>
                                            Créer l'avenant
                                        </Button>
                                    </DialogFooter>
                                </DialogContent>
                            </Dialog>
                        )}
                    </div>
                )}
            </div>

            {/* Progress */}
            <Card>
                <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-medium">Progression du mandat</span>
                        <span className="text-sm text-muted-foreground">
                            {getProgressValue(mandate.status)}%
                        </span>
                    </div>
                    <Progress value={getProgressValue(mandate.status)} className="h-2" />
                </CardContent>
            </Card>

            {/* Main content */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                <TabsList className="w-full justify-start overflow-x-auto scrollbar-hide flex-nowrap">
                    <TabsTrigger value="details" data-testid="tab-details" className="flex-shrink-0">Détails</TabsTrigger>
                    <TabsTrigger value="sellers" data-testid="tab-sellers" className="flex-shrink-0">Vendeurs</TabsTrigger>
                    <TabsTrigger value="dvf" data-testid="tab-dvf" className="flex-shrink-0">Marché DVF</TabsTrigger>
                    <TabsTrigger value="timeline" data-testid="tab-timeline" className="flex-shrink-0">Historique</TabsTrigger>
                    <TabsTrigger value="documents" data-testid="tab-documents" className="flex-shrink-0">Documents</TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                        {/* Property info */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Home className="w-5 h-5" />
                                    Informations du bien
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Prix</p>
                                        <p className="text-xl font-bold text-primary">{formatCurrency(mandate.price)}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground">Commission</p>
                                        <p className="text-xl font-bold">{mandate.commission_rate}%</p>
                                    </div>
                                </div>
                                <Separator />
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Type de mandat</p>
                                        <p className="font-medium">{MANDATE_TYPE_LABELS[mandate.mandate_type || mandate.type_mandat] || 'Non renseigné'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground">Durée</p>
                                        <p className="font-medium">{mandate.duration_months ? `${mandate.duration_months} mois` : 'Non renseigné'}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Dates */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Calendar className="w-5 h-5" />
                                    Dates
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <p className="text-sm text-muted-foreground">Création</p>
                                    <p className="font-medium">{formatDateTime(mandate.created_at)}</p>
                                </div>
                                {mandate.start_date && (
                                    <div>
                                        <p className="text-sm text-muted-foreground">Début du mandat</p>
                                        <p className="font-medium">{formatDate(mandate.start_date)}</p>
                                    </div>
                                )}
                                {mandate.end_date && (
                                    <div>
                                        <p className="text-sm text-muted-foreground">Fin prévue</p>
                                        <p className="font-medium">{formatDate(mandate.end_date)}</p>
                                    </div>
                                )}
                                {mandate.notes && (
                                    <>
                                        <Separator />
                                        <div>
                                            <p className="text-sm text-muted-foreground">Notes</p>
                                            <p className="text-sm">{mandate.notes}</p>
                                        </div>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                <TabsContent value="sellers">
                    <Card>
                        <CardHeader>
                            <CardTitle>Vendeurs ({mandate.sellers.length})</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {mandate.sellers.map((seller, index) => (
                                    <div key={index} className="flex items-center gap-4 p-4 rounded-lg bg-muted/50">
                                        <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                                            {seller.first_name[0]}{seller.last_name[0]}
                                        </div>
                                        <div className="flex-1">
                                            <p className="font-semibold">{seller.first_name} {seller.last_name}</p>
                                            <p className="text-sm text-muted-foreground">{seller.email}</p>
                                            {seller.phone && (
                                                <p className="text-sm text-muted-foreground">{seller.phone}</p>
                                            )}
                                        </div>
                                        {(isAdmin || isAgent) && (
                                            <Button variant="outline" size="sm">
                                                <MessageSquare className="w-4 h-4 mr-2" />
                                                Contacter
                                            </Button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="dvf">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Home className="w-5 h-5" />
                                Analyse de marché DVF (base officielle gov)
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <DVFAnalyzer
                                address={mandate.property?.address || mandate.address}
                                postalCode={mandate.property?.postal_code || mandate.postal_code}
                                propertyType={(() => {
                                    const t = String(mandate.property?.property_type || mandate.property_type || '').toLowerCase();
                                    if (t.includes('appart')) return 'apartment';
                                    if (t.includes('maison') || t.includes('house')) return 'house';
                                    if (t.includes('terrain') || t.includes('land')) return 'land';
                                    return 'apartment';
                                })()}
                                surfaceM2={mandate.property?.surface || mandate.surface}
                                rooms={mandate.property?.rooms || mandate.rooms}
                                askingPrice={mandate.price}
                                autoRun
                            />
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="timeline">
                    <Card>
                        <CardHeader>
                            <CardTitle>Historique</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {timeline.length === 0 ? (
                                <p className="text-center text-muted-foreground py-8">
                                    Aucun événement pour le moment
                                </p>
                            ) : (
                                <div className="space-y-4">
                                    {timeline.map((event, index) => (
                                        <div key={index} className="timeline-item">
                                            <div className="timeline-dot" />
                                            <div className="pl-4">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium capitalize">
                                                        {event.action.replace(/_/g, ' ')}
                                                    </span>
                                                    <Badge variant="outline" className="text-xs">
                                                        {event.type}
                                                    </Badge>
                                                </div>
                                                <p className="text-sm text-muted-foreground">
                                                    {formatDateTime(event.date)}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="documents">
                    <Card>
                        <CardHeader>
                            <CardTitle>Documents</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                                    <div className="flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                            <FileText className="w-5 h-5 text-primary" />
                                        </div>
                                        <div>
                                            <p className="font-medium">Mandat de vente</p>
                                            <p className="text-sm text-muted-foreground">
                                                {mandate.status === 'brouillon' ? 'En attente de signature' :
                                                 ['en_signature', 'a_signer'].includes(mandate.status) ? 'En cours de signature' :
                                                 'Signé'}
                                            </p>
                                        </div>
                                    </div>
                                    <Button variant="outline" size="sm" data-testid="download-mandate-button">
                                        <Download className="w-4 h-4 mr-2" />
                                        Télécharger
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Make Offer Dialog */}
            <MakeOfferDialog
                open={showMakeOfferDialog}
                onOpenChange={setShowMakeOfferDialog}
                mandateId={mandate.id}
                mandateNumber={mandate.mandate_number}
                onOfferCreated={loadMandate}
            />
        </div>
    );
}

export default MandateDetailPage;
